import { cn } from '../../lib/cn'
import { TEXTOS } from '../../lib/glossario'
import type { SituacaoDeSla } from '../../types/chamado'
import { percentualConsumido } from './sla'

interface Props {
  abertoEm: string
  /** Prazo de solução vindo do e-Niterói. Nulo em serviço agendado e em chamado sem SLA. */
  prazoSla: string | null
  /** Nulo enquanto aberto — aí a barra mede até agora. */
  fechadoEm: string | null
  situacao: SituacaoDeSla
}

/**
 * Quanto do prazo já foi consumido, do verde ao vermelho — como na tela do e-Niterói.
 *
 * **Os limiares não foram inventados aqui: são os que o e-Niterói tem configurado.** A sessão da
 * API devolve `glpiduedatewarning_less: 20` e `glpiduedatecritical_less: 5`, ambos em porcentagem,
 * com as cores de alerta e de crítico. Então a regra é a mesma que o atendente vê do outro lado:
 * verde até restarem 20% do prazo, âmbar de 20% a 5%, vermelho nos últimos 5% e depois do
 * estouro. Fiscal e atendente olhando o mesmo chamado veem o mesmo estado — divergir disso criaria
 * discussão sobre a cor em vez de sobre o atendimento.
 *
 * As cores saem da paleta do sistema, não do hexadecimal cru do e-Niterói (`#06ff00` e `#ff0000`):
 * verde-limão puro sobre branco não passa em contraste, e esta tela é lida por quem tem de conferir
 * número, não por quem está de passagem.
 *
 * **A porcentagem vai escrita ao lado da barra, sempre.** É a mesma regra da etiqueta de prazo:
 * cor sozinha não informa quem não distingue vermelho de cinza, e aqui o dado é justamente o
 * quanto — "quase estourando" e "estourou" são decisões diferentes.
 *
 * **O que esta barra NÃO é:** a medida contratual. O TR conta o SLA em horas úteis e para o
 * relógio enquanto o chamado está "Pendente" (ANEXO IV p.90, definições 7 e 8; nota 4a, p.161-162).
 * Aqui é tempo corrido entre abertura e prazo, que é o que o e-Niterói desenha. Serve para o olho
 * achar o chamado apertado; quem assina glosa usa o fechamento do mês.
 *
 * Em `SEM_PRAZO` não há barra — nem cinza, nem vazia. Barra vazia se lê como "prazo inteiro pela
 * frente", e o que existe ali é ausência de prazo: serviço agendado, com SLA entre asteriscos.
 *
 * @param props abertura, prazo, fechamento e a situação já apurada pelo servidor
 * @returns a barra de consumo, ou nada quando não há prazo a consumir
 */
export function BarraDeSla({ abertoEm, prazoSla, fechadoEm, situacao }: Props) {
  const consumido = percentualConsumido(abertoEm, prazoSla, fechadoEm)
  if (situacao === 'SEM_PRAZO' || consumido === null) {
    return null
  }

  const estourado = situacao === 'ESTOURADO' || consumido >= 100
  const restante = 100 - consumido
  const cor = estourado || restante <= 5 ? 'bg-red-600' : restante <= 20 ? 'bg-amber-500' : 'bg-green-600'
  const arredondado = Math.round(consumido)
  const descricao = estourado
    ? TEXTOS.barraDeSla.estourado
    : `${arredondado}% ${TEXTOS.barraDeSla.consumido}`

  return (
    <span className="mt-1.5 flex items-center gap-2" title={TEXTOS.barraDeSla.ajuda}>
      <span
        role="progressbar"
        aria-label={TEXTOS.barraDeSla.rotulo}
        aria-valuenow={arredondado}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={descricao}
        className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-slate-200"
      >
        <span className={cn('block h-full rounded-full', cor)} style={{ width: `${Math.min(consumido, 100)}%` }} />
      </span>
      {/* `aria-hidden`: a barra acima já anuncia o mesmo pela `aria-valuetext`, e sem isto o leitor
          de tela leria a porcentagem duas vezes em cada linha da tabela. */}
      <span aria-hidden className="text-xs tabular-nums text-slate-500">
        {estourado ? '100%' : `${arredondado}%`}
      </span>
    </span>
  )
}
